/**
 * Global fetch patch: adds Authorization: Bearer <token> header to every
 * same-origin (or REACT_APP_BACKEND_URL) request whenever a session token
 * exists in localStorage.
 *
 * This is a fallback for mobile browsers that strip / block the
 * `session_token` cookie (iOS Safari ITP, third-party-cookie blocking on
 * Android Chrome after the redirect from auth.emergentagent.com). On those
 * devices the cookie never sticks, but the token in localStorage does.
 *
 * Desktop browsers continue to use the cookie (they always send both the
 * cookie AND the header — backend takes whichever is present). Auth still
 * works seamlessly even if a user clears localStorage.
 *
 * Call once from the app entry point (index.js / App.js) BEFORE any other
 * code makes fetch calls.
 */
const STORAGE_KEY = 'gs_session_token';
const BUILD_API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BUILD_API_HOST = (() => {
  try {
    return new URL(BUILD_API_URL).host;
  } catch {
    return '';
  }
})();
const CURRENT_HOST = (typeof window !== 'undefined' ? window.location.host : '');
// If the build embedded a different host than the page is served from
// (e.g., custom domain qmcrm.com served from a build with REACT_APP_BACKEND_URL
// pointing to lead-intake-hub-1.emergent.host), iOS Safari refuses the
// cross-origin POST during the OAuth redirect handoff with
// "The object can not be cloned." Same-origin rewrite avoids this.
const SHOULD_REWRITE_TO_SAME_ORIGIN = (
  BUILD_API_HOST &&
  CURRENT_HOST &&
  BUILD_API_HOST !== CURRENT_HOST
);
const API_HOST = BUILD_API_HOST || CURRENT_HOST;

export function setStoredAuthToken(token) {
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    /* localStorage disabled — fall back to cookie-only auth */
    if (typeof console !== 'undefined') console.warn('[authFetch] setStoredAuthToken:', err?.message || err);
  }
}

export function getStoredAuthToken() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[authFetch] getStoredAuthToken:', err?.message || err);
    return '';
  }
}

export function clearStoredAuthToken() {
  setStoredAuthToken(null);
}

function maybeRewriteToSameOrigin(url) {
  if (!SHOULD_REWRITE_TO_SAME_ORIGIN || !url || typeof url !== 'string') return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.host === BUILD_API_HOST) {
      // Replace cross-origin host with current page origin. The custom domain
      // proxy forwards /api/* to the same backend, so this works transparently
      // and avoids iOS Safari dropping the cross-origin POST.
      parsed.protocol = window.location.protocol;
      parsed.host = window.location.host;
      return parsed.toString();
    }
  } catch (err) {
    /* fall through */
    if (typeof console !== 'undefined') console.warn('[authFetch] URL parse failed:', err?.message || err);
  }
  return url;
}

let installed = false;
export function installAuthFetchPatch() {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init = {}) => {
    try {
      let resolvedInput = input;
      let url = typeof input === 'string' ? input : (input && input.url) || '';

      // Rewrite cross-origin API URLs to same-origin (mobile Safari fix)
      const rewritten = maybeRewriteToSameOrigin(url);
      if (rewritten !== url) {
        url = rewritten;
        // We only ever pass strings into fetch from our own code, so a string
        // swap is sufficient. If a Request object was passed, fall back to its
        // original URL to avoid mangling its body stream.
        if (typeof input === 'string') {
          resolvedInput = rewritten;
        }
      }

      // Only attach the header on same-origin / API host requests so we never
      // leak the token to unrelated third-party domains.
      let host = '';
      try {
        host = new URL(url, window.location.origin).host;
      } catch {
        host = window.location.host;
      }
      const isOurApi = host === API_HOST || host === window.location.host;
      const token = isOurApi ? getStoredAuthToken() : '';

      if (token) {
        // Build merged headers without clobbering anything the caller set.
        const headers = new Headers(init.headers || {});
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        return originalFetch(resolvedInput, { ...init, headers });
      }
      return originalFetch(resolvedInput, init);
    } catch (err) {
      /* If anything goes wrong, fall through to a normal fetch */
      if (typeof console !== 'undefined') console.warn('[authFetch] patch failed, using bare fetch:', err?.message || err);
    }
    return originalFetch(input, init);
  };
}
